import axios from 'axios';
import { db } from '../../config/firebase';
import {
  companyFailed,
  companyPending,
  setCompanyData,
  setTeamRoster,
} from '../reducers/company.slice';
import { loginSuccess } from '../reducers/auth.slice';
import {
  INVITABLE_ROLES,
  WORKSPACE_ROLES,
  isAdmin,
  normalizeWorkspaceRole,
  roleLabel,
} from 'app/main/apps/nurturer-platform/lib/permissions';
import {
  hasProduct,
  isWorkspaceProductId,
} from 'app/main/apps/nurturer-platform/lib/products';

const SENDGRID_EMAIL_URL = 'https://nurturer-sendgrid-backend.vercel.app/send-email';

function createToken() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function appOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://www.nurturer.ai';
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isInviteExpired(expiresAt) {
  const date = toDate(expiresAt);
  if (!date) return false;
  return date.getTime() < Date.now();
}

function normalizeCompany(company, id) {
  const data = company || {};
  return {
    id,
    ...data,
    enabledProducts: Array.isArray(data.enabledProducts) ? data.enabledProducts : [],
    subscription: Array.isArray(data.subscription) ? data.subscription : [],
  };
}

async function sendTeamInviteEmail({
  to,
  inviterName,
  workspaceName,
  role,
  companyID,
  token,
}) {
  const origin = appOrigin();
  const joinUrl = `${origin}/register/${companyID}?invite=${token}`;
  const loginUrl = `${origin}/login?invite=${token}&companyID=${encodeURIComponent(companyID)}`;
  const roleText = roleLabel(role);
  const roleDesc =
    role === WORKSPACE_ROLES.ADMIN
      ? 'Full access to settings, team, products, and billing'
      : 'Access to nurture contacts, touches, and enabled products';

  const htmlMessage = `
    <h2>You're invited to ${workspaceName} on Nurturer</h2>
    <p>${inviterName} invited you to join <strong>${workspaceName}</strong> as <strong>${roleText}</strong>.</p>
    <p>${roleDesc}.</p>
    <p><a href="${joinUrl}" style="display:inline-block;padding:12px 18px;background:#0C8587;color:#fff;text-decoration:none;border-radius:8px;font-weight:600;">Accept invite</a></p>
    <p>Or open this link:<br/><a href="${joinUrl}">${joinUrl}</a></p>
    <p>Already have a Nurturer account? <a href="${loginUrl}">Log in to accept</a>.</p>
    <p style="color:#6C766F;font-size:13px;">This invite expires in 7 days.</p>
    <p>Warm regards,<br/>The Nurturer Team</p>
  `;

  await axios.post(
    SENDGRID_EMAIL_URL,
    {
      to,
      subject: `${inviterName} invited you to ${workspaceName} on Nurturer`,
      htmlMessage,
      name: to,
      userEmail: 'info@nurturer.ai',
    },
    { headers: { 'Content-Type': 'application/json' } }
  );

  return { joinUrl, loginUrl, invitePath: `/register/${companyID}?invite=${token}` };
}

function initialsFrom(name, email) {
  const source = (name || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

async function resolveCompany(companyID) {
  if (!companyID || !db) return null;

  const byId = await db.collection('companies').doc(companyID).get();
  if (byId.exists) {
    return {
      companyDocId: byId.id,
      company: normalizeCompany(byId.data(), byId.id),
    };
  }

  const byField = await db
    .collection('companies')
    .where('companyID', '==', companyID)
    .limit(1)
    .get();

  if (!byField.empty) {
    const doc = byField.docs[0];
    return {
      companyDocId: doc.id,
      company: normalizeCompany(doc.data(), doc.id),
    };
  }

  return null;
}

function requireAdmin(user) {
  if (!isAdmin(user)) {
    return 'Only admins can do that.';
  }
  return null;
}

export const fetchCompanyForUser = (user) => async (dispatch) => {
  const companyID = user?.companyID || user?.companyId;
  if (!companyID || !db) {
    dispatch(setCompanyData({ company: null, companyDocId: null }));
    return null;
  }

  dispatch(companyPending());
  try {
    const resolved = await resolveCompany(companyID);
    if (!resolved) {
      dispatch(
        setCompanyData({
          company: normalizeCompany({ companyID }, null),
          companyDocId: null,
        })
      );
      return null;
    }

    if (resolved.companyDocId) {
      const snap = await db.collection('companies').doc(resolved.companyDocId).get();
      const raw = snap.data() || {};
      if (!Array.isArray(raw.enabledProducts)) {
        try {
          await db
            .collection('companies')
            .doc(resolved.companyDocId)
            .update({ enabledProducts: [] });
          resolved.company = normalizeCompany(
            { ...raw, enabledProducts: [] },
            resolved.companyDocId
          );
        } catch (writeError) {
          console.warn('Could not initialize enabledProducts', writeError);
        }
      }
    }

    dispatch(setCompanyData(resolved));
    return resolved;
  } catch (error) {
    console.error('fetchCompanyForUser failed', error);
    dispatch(companyFailed(error.message));
    return null;
  }
};

export const fetchTeamRoster = (user) => async (dispatch) => {
  const companyID = user?.companyID || user?.companyId;
  if (!companyID || !db) {
    dispatch(setTeamRoster({ members: [], pendingInvites: [] }));
    return;
  }

  dispatch(companyPending());
  try {
    const resolved = await resolveCompany(companyID);
    const membersSnap = await db
      .collection('users')
      .where('companyID', '==', companyID)
      .get();

    const members = membersSnap.docs.map((doc) => {
      const data = doc.data();
      const role = normalizeWorkspaceRole(data.workspaceRole);
      return {
        id: doc.id,
        uid: data.uid || doc.id,
        name: data.name || data.displayName || data.email || 'User',
        email: data.email || '',
        role,
        roleLabel: roleLabel(role),
        isYou: (data.uid || doc.id) === user.uid,
        photoUrl: data.photoUrl || '',
        avatar: {
          initials: initialsFrom(data.name || data.displayName, data.email),
        },
      };
    });

    let pendingInvites = [];
    if (resolved?.companyDocId) {
      const invitesSnap = await db
        .collection('companies')
        .doc(resolved.companyDocId)
        .collection('teamInvites')
        .where('status', '==', 'PENDING')
        .get();

      pendingInvites = invitesSnap.docs.map((doc) => {
        const data = doc.data();
        const role = normalizeWorkspaceRole(data.role);
        return {
          id: doc.id,
          email: data.email || '',
          role,
          roleLabel: roleLabel(role),
          token: data.token,
          avatar: { initials: initialsFrom(null, data.email) },
        };
      });
    }

    if (resolved) {
      dispatch(setCompanyData(resolved));
    }
    dispatch(setTeamRoster({ members, pendingInvites }));
  } catch (error) {
    console.error('fetchTeamRoster failed', error);
    dispatch(companyFailed(error.message));
  }
};

export const inviteTeamMember =
  ({ email, role }, user) =>
  async (dispatch) => {
    const adminError = requireAdmin(user);
    if (adminError) return { ok: false, error: adminError };

    const companyID = user?.companyID || user?.companyId;
    const normalizedEmail = String(email || '')
      .trim()
      .toLowerCase();
    const inviteRole = normalizeWorkspaceRole(role);

    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      return { ok: false, error: 'Enter a valid email.' };
    }
    if (!INVITABLE_ROLES.includes(inviteRole)) {
      return { ok: false, error: 'Invalid role.' };
    }

    try {
      const resolved = await resolveCompany(companyID);
      if (!resolved?.companyDocId) {
        return { ok: false, error: 'Company not found. Contact support.' };
      }

      const existing = await db
        .collection('users')
        .where('email', '==', normalizedEmail)
        .limit(1)
        .get();
      if (!existing.empty) {
        const existingUser = existing.docs[0].data();
        if (
          existingUser.companyID === companyID ||
          existingUser.companyId === companyID
        ) {
          return { ok: false, error: 'That person is already on this team.' };
        }
      }

      const invitesRef = db
        .collection('companies')
        .doc(resolved.companyDocId)
        .collection('teamInvites');

      const pendingSnap = await invitesRef.where('email', '==', normalizedEmail).get();
      const pendingDoc = pendingSnap.docs.find((doc) => doc.data().status === 'PENDING');

      const token = createToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      if (pendingDoc) {
        // Same email already pending: revoke old token doc and write a fresh one.
        await pendingDoc.ref.update({ status: 'REVOKED', updatedAt: new Date() });
      }

      await invitesRef.doc(token).set({
        email: normalizedEmail,
        role: inviteRole,
        token,
        status: 'PENDING',
        invitedById: user.uid,
        companyID,
        createdAt: new Date(),
        expiresAt,
      });

      const workspaceName =
        resolved.company.name ||
        resolved.company.companyName ||
        companyID ||
        'your workspace';

      let emailResult;
      try {
        emailResult = await sendTeamInviteEmail({
          to: normalizedEmail,
          inviterName: user.name || user.email || 'A teammate',
          workspaceName,
          role: inviteRole,
          companyID,
          token,
        });
      } catch (emailError) {
        console.error('Team invite email failed', emailError);
        dispatch(fetchTeamRoster(user));
        return {
          ok: true,
          emailSent: false,
          invitePath: `/register/${companyID}?invite=${token}`,
          error: 'Invite saved, but the email failed to send. Share the link manually.',
        };
      }

      dispatch(fetchTeamRoster(user));
      return {
        ok: true,
        emailSent: true,
        invitePath: emailResult.invitePath,
      };
    } catch (error) {
      console.error('inviteTeamMember failed', error);
      return { ok: false, error: error.message || 'Could not send invite.' };
    }
  };

export const revokeTeamInvite =
  (inviteId, user) =>
  async (dispatch) => {
    const adminError = requireAdmin(user);
    if (adminError) return { ok: false, error: adminError };

    try {
      const resolved = await resolveCompany(user?.companyID || user?.companyId);
      if (!resolved?.companyDocId) {
        return { ok: false, error: 'Company not found.' };
      }

      await db
        .collection('companies')
        .doc(resolved.companyDocId)
        .collection('teamInvites')
        .doc(inviteId)
        .update({ status: 'REVOKED' });

      dispatch(fetchTeamRoster(user));
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || 'Could not revoke invite.' };
    }
  };

export const updateMemberRole =
  (memberId, role, user) =>
  async (dispatch, getState) => {
    const adminError = requireAdmin(user);
    if (adminError) return { ok: false, error: adminError };

    const nextRole = normalizeWorkspaceRole(role);
    if (!INVITABLE_ROLES.includes(nextRole)) {
      return { ok: false, error: 'Invalid role.' };
    }

    try {
      const { members } = getState().company || {};
      const member = (members || []).find((m) => m.id === memberId);
      if (!member) return { ok: false, error: 'Member not found.' };

      if (
        member.role === WORKSPACE_ROLES.ADMIN &&
        nextRole !== WORKSPACE_ROLES.ADMIN
      ) {
        const adminCount = (members || []).filter(
          (m) => m.role === WORKSPACE_ROLES.ADMIN
        ).length;
        if (adminCount <= 1) {
          return { ok: false, error: 'Cannot demote the last admin.' };
        }
      }

      await db.collection('users').doc(memberId).update({
        workspaceRole: nextRole,
      });

      if (member.uid === user.uid) {
        dispatch(
          loginSuccess({
            user: { ...user, workspaceRole: nextRole },
            uid: user.uid,
          })
        );
      }

      dispatch(fetchTeamRoster(user));
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || 'Could not update role.' };
    }
  };

export const removeTeamMember =
  (memberId, user) =>
  async (dispatch, getState) => {
    const adminError = requireAdmin(user);
    if (adminError) return { ok: false, error: adminError };

    try {
      const { members } = getState().company || {};
      const member = (members || []).find((m) => m.id === memberId);
      if (!member) return { ok: false, error: 'Member not found.' };
      if (member.uid === user.uid) {
        return { ok: false, error: 'You cannot remove yourself.' };
      }

      if (member.role === WORKSPACE_ROLES.ADMIN) {
        const adminCount = (members || []).filter(
          (m) => m.role === WORKSPACE_ROLES.ADMIN
        ).length;
        if (adminCount <= 1) {
          return { ok: false, error: 'Cannot remove the last admin.' };
        }
      }

      await db.collection('users').doc(memberId).update({
        companyID: null,
        workspaceRole: WORKSPACE_ROLES.MEMBER,
      });

      dispatch(fetchTeamRoster(user));
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || 'Could not remove member.' };
    }
  };

export const setProductEnabled =
  (productId, enabled, user) =>
  async (dispatch) => {
    const adminError = requireAdmin(user);
    if (adminError) return { ok: false, error: adminError };
    if (!isWorkspaceProductId(productId)) {
      return { ok: false, error: 'Invalid product.' };
    }

    try {
      const companyID = user?.companyID || user?.companyId;
      const resolved = await resolveCompany(companyID);
      if (!resolved?.companyDocId) {
        return { ok: false, error: 'Company not found. Contact support.' };
      }

      const current = resolved.company.enabledProducts || [];
      const next = enabled
        ? hasProduct(current, productId)
          ? current
          : [...current, productId]
        : current.filter((id) => id !== productId);

      await db.collection('companies').doc(resolved.companyDocId).update({
        enabledProducts: next,
      });

      dispatch(
        setCompanyData({
          companyDocId: resolved.companyDocId,
          company: { ...resolved.company, enabledProducts: next },
        })
      );
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || 'Could not update products.' };
    }
  };

export const resolveDefaultSignupRole = async (companyID) => {
  if (!db || !companyID) return WORKSPACE_ROLES.ADMIN;

  try {
    const membersSnap = await db
      .collection('users')
      .where('companyID', '==', companyID)
      .get();

    if (membersSnap.empty) {
      return WORKSPACE_ROLES.ADMIN;
    }

    const hasAdmin = membersSnap.docs.some((doc) => {
      const role = doc.data().workspaceRole;
      return normalizeWorkspaceRole(role) === WORKSPACE_ROLES.ADMIN;
    });

    return hasAdmin ? WORKSPACE_ROLES.MEMBER : WORKSPACE_ROLES.ADMIN;
  } catch (error) {
    console.error('resolveDefaultSignupRole failed', error);
    return WORKSPACE_ROLES.MEMBER;
  }
};

export const resolveTeamInvite = async ({ companyID, inviteToken, email }) => {
  if (!db || !companyID || !inviteToken) {
    return { workspaceRole: WORKSPACE_ROLES.MEMBER, invalid: true };
  }

  try {
    const resolved = await resolveCompany(companyID);
    if (!resolved?.companyDocId) {
      return { workspaceRole: WORKSPACE_ROLES.MEMBER, invalid: true };
    }

    const inviteDoc = await db
      .collection('companies')
      .doc(resolved.companyDocId)
      .collection('teamInvites')
      .doc(inviteToken)
      .get();

    if (!inviteDoc.exists) {
      return { workspaceRole: WORKSPACE_ROLES.MEMBER, invalid: true };
    }

    const invite = inviteDoc.data();
    if (invite.status !== 'PENDING') {
      return { workspaceRole: WORKSPACE_ROLES.MEMBER, invalid: true };
    }

    if (isInviteExpired(invite.expiresAt)) {
      try {
        await inviteDoc.ref.update({ status: 'EXPIRED' });
      } catch (_) {
        /* ignore */
      }
      return { workspaceRole: WORKSPACE_ROLES.MEMBER, expired: true };
    }

    if (
      invite.email &&
      email &&
      invite.email.toLowerCase() !== String(email).toLowerCase()
    ) {
      return { workspaceRole: WORKSPACE_ROLES.MEMBER, inviteMismatch: true };
    }

    return {
      workspaceRole: normalizeWorkspaceRole(invite.role),
      inviteRefPath: inviteDoc.ref.path,
      inviteEmail: invite.email || '',
      companyID: invite.companyID || companyID,
      companyDocId: resolved.companyDocId,
    };
  } catch (error) {
    console.error('resolveTeamInvite failed', error);
    return { workspaceRole: WORKSPACE_ROLES.MEMBER, invalid: true };
  }
};

export const markTeamInviteAccepted = async (inviteRefPath) => {
  if (!db || !inviteRefPath) return;
  try {
    await db.doc(inviteRefPath).update({
      status: 'ACCEPTED',
      acceptedAt: new Date(),
    });
  } catch (error) {
    console.error('markTeamInviteAccepted failed', error);
  }
};

/**
 * Existing account accepts a team invite after login.
 */
export const acceptTeamInviteForExistingUser =
  ({ companyID, inviteToken }, user) =>
  async (dispatch) => {
    if (!user?.uid) {
      return { ok: false, error: 'Not signed in.' };
    }

    const invite = await resolveTeamInvite({
      companyID,
      inviteToken,
      email: user.email,
    });

    if (invite.expired) {
      return { ok: false, error: 'This invite has expired. Ask an admin to invite you again.' };
    }
    if (invite.inviteMismatch) {
      return {
        ok: false,
        error: 'This invite was sent to a different email address.',
      };
    }
    if (invite.invalid || !invite.inviteRefPath) {
      return { ok: false, error: 'This invite is invalid or no longer pending.' };
    }

    try {
      const nextRole = normalizeWorkspaceRole(invite.workspaceRole);
      await db.collection('users').doc(user.uid).update({
        companyID: invite.companyID || companyID,
        workspaceRole: nextRole,
      });
      await markTeamInviteAccepted(invite.inviteRefPath);

      const nextUser = {
        ...user,
        companyID: invite.companyID || companyID,
        workspaceRole: nextRole,
      };
      dispatch(loginSuccess({ user: nextUser, uid: user.uid }));
      dispatch(fetchCompanyForUser(nextUser));
      return { ok: true };
    } catch (error) {
      console.error('acceptTeamInviteForExistingUser failed', error);
      return { ok: false, error: error.message || 'Could not accept invite.' };
    }
  };

/** @deprecated use resolveTeamInvite + markTeamInviteAccepted */
export const acceptTeamInviteOnSignup = async (args) => resolveTeamInvite(args);
